// Filtros de «Elige el teléfono»: búsqueda, marca, banda de precio, financiera y orden, con el
// contador y los filtros activos como chips quitables. Todo se aplica en memoria (`catalogFilters.ts`).
import { useId } from 'react';
import { RefreshCw, Search, X } from 'lucide-react';
import { Button, Select } from '@/kit';
import { cn } from '@/lib/cn';
import { financierName } from '@/domains/credit/origination/lib/originationMetrics';
import {
    CATALOG_SORTS,
    PRICE_BANDS,
    SIN_FILTRO,
    hayFiltrosActivos,
    type CatalogFilterState,
    type CatalogSort,
} from '../catalogFilters';

/** Chip de un filtro activo, con su X para quitarlo sin tocar los demás. */
function ChipFiltro({ label, onClear }: { label: string; onClear: () => void }) {
    return (
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background py-0.5 pl-2 pr-1 text-[11px]">
            {label}
            <button
                type="button"
                onClick={onClear}
                aria-label={`Quitar filtro ${label}`}
                className="flex size-4 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
                <X className="size-3" aria-hidden />
            </button>
        </span>
    );
}

/** Select del kit con su etiqueta visible arriba, para que «Todas» se lea sin cortarse. */
function LabeledSelect({
    label,
    value,
    onChange,
    options,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
}) {
    const id = useId();
    return (
        <div className="flex min-w-0 flex-col gap-1">
            <label
                htmlFor={id}
                className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
                {label}
            </label>
            <Select id={id} value={value} onChange={onChange} options={options} />
        </div>
    );
}

export function CatalogToolbar({
    filters,
    onChange,
    brands,
    financiers,
    shown,
    total,
    searchRef,
    onRefresh,
    refreshing,
}: {
    filters: CatalogFilterState;
    onChange: (next: CatalogFilterState) => void;
    brands: string[];
    /** Códigos de financiera presentes en el catálogo. */
    financiers: string[];
    /** Cuántos quedaron tras filtrar. */
    shown: number;
    /** Cuántos equipos se ofrecen. */
    total: number;
    searchRef?: React.Ref<HTMLInputElement>;
    onRefresh: () => void;
    refreshing: boolean;
}) {
    const set = (patch: Partial<CatalogFilterState>): void => {
        onChange({ ...filters, ...patch });
    };
    const banda = PRICE_BANDS.find((b) => b.value === filters.band);
    const activos = hayFiltrosActivos(filters);

    return (
        <div className="mb-4 rounded-lg border border-border bg-card p-3">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-0 flex-1 basis-full sm:basis-[240px]">
                    <Search
                        className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                    />
                    <input
                        ref={searchRef}
                        type="search"
                        aria-label="Buscar teléfono"
                        value={filters.q}
                        onChange={(e) => {
                            set({ q: e.target.value });
                        }}
                        placeholder="Busca por marca o modelo"
                        className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-2.5 text-[12.5px] outline-none focus:border-ring"
                    />
                </div>

                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRefresh}
                    disabled={refreshing}
                    leftIcon={
                        <RefreshCw
                            className={cn('size-3.5', refreshing && 'animate-spin')}
                            aria-hidden
                        />
                    }
                >
                    Actualizar
                </Button>

                {/* aria-live: al filtrar, el lector de pantalla anuncia cuántos quedaron. */}
                <div
                    className="text-[11.5px] tabular-nums text-muted-foreground"
                    aria-live="polite"
                >
                    {shown === total
                        ? `${String(total)} ${total === 1 ? 'equipo' : 'equipos'}`
                        : `${String(shown)} de ${String(total)} equipos`}
                </div>
            </div>

            <div
                className={cn(
                    'mt-2 grid grid-cols-2 gap-2',
                    financiers.length > 0 ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
                )}
            >
                <LabeledSelect
                    label="Marca"
                    value={filters.brand}
                    onChange={(brand) => {
                        set({ brand });
                    }}
                    options={[
                        { value: SIN_FILTRO, label: 'Todas' },
                        ...brands.map((b) => ({ value: b, label: b })),
                    ]}
                />

                <LabeledSelect
                    label="Precio"
                    value={filters.band}
                    onChange={(band) => {
                        set({ band });
                    }}
                    options={PRICE_BANDS.map((b) => ({ value: b.value, label: b.label }))}
                />

                {financiers.length > 0 && (
                    <LabeledSelect
                        label="Financiera"
                        value={filters.financier}
                        onChange={(financier) => {
                            set({ financier });
                        }}
                        options={[
                            { value: SIN_FILTRO, label: 'Todas' },
                            ...financiers.map((f) => ({ value: f, label: financierName(f) })),
                        ]}
                    />
                )}

                <LabeledSelect
                    label="Orden"
                    value={filters.sort}
                    onChange={(sort) => {
                        set({ sort: sort as CatalogSort });
                    }}
                    options={CATALOG_SORTS}
                />
            </div>

            {activos && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
                    {filters.q.trim() !== '' && (
                        <ChipFiltro
                            label={`«${filters.q.trim()}»`}
                            onClear={() => {
                                set({ q: '' });
                            }}
                        />
                    )}
                    {filters.brand !== SIN_FILTRO && (
                        <ChipFiltro
                            label={filters.brand}
                            onClear={() => {
                                set({ brand: SIN_FILTRO });
                            }}
                        />
                    )}
                    {filters.band !== SIN_FILTRO && banda && (
                        <ChipFiltro
                            label={banda.label}
                            onClear={() => {
                                set({ band: SIN_FILTRO });
                            }}
                        />
                    )}
                    {filters.financier !== SIN_FILTRO && (
                        <ChipFiltro
                            label={financierName(filters.financier)}
                            onClear={() => {
                                set({ financier: SIN_FILTRO });
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
